// i18n 초기화 — 한국어(기본) / 영어.
//
// [fallbackLng 를 'ko' 로 두는 이유]
// 영어 번역이 비어 있는 키는 한국어가 그대로 나온다. 화면이 깨지지 않으면서
// 미번역 지점이 눈에 띄어 관리된다. 반대로 fallback 을 'en' 으로 두면 번역이
// 덜 된 화면에서 한국어 사용자가 영어를 보게 된다 — 회귀다.
//
// [네임스페이스를 나누는 이유]
// 파일 하나에 2,000개가 넘는 문구가 쌓이면 사람이 검수할 수 없다. 기능 단위로 쪼개
// 화면 단위 전환·리뷰가 가능하게 한다.
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import koCommon from './locales/ko/common.json';
import koMenu from './locales/ko/menu.json';
import koEditor from './locales/ko/editor.json';
import koConnection from './locales/ko/connection.json';
import koMonitoring from './locales/ko/monitoring.json';
import koChallenge from './locales/ko/challenge.json';
import koDataTransfer from './locales/ko/dataTransfer.json';
import koSettings from './locales/ko/settings.json';
import koAuth from './locales/ko/auth.json';
import koAssistant from './locales/ko/assistant.json';

import enCommon from './locales/en/common.json';
import enMenu from './locales/en/menu.json';
import enEditor from './locales/en/editor.json';
import enConnection from './locales/en/connection.json';
import enMonitoring from './locales/en/monitoring.json';
import enChallenge from './locales/en/challenge.json';
import enDataTransfer from './locales/en/dataTransfer.json';
import enSettings from './locales/en/settings.json';
import enAuth from './locales/en/auth.json';
import enAssistant from './locales/en/assistant.json';

export const SUPPORTED_LANGUAGES = [
    { code: 'ko', label: '한국어' },
    { code: 'en', label: 'English' },
];

export const NAMESPACES = ['common', 'menu', 'editor', 'connection', 'monitoring', 'challenge', 'dataTransfer', 'settings', 'auth', 'assistant'];

const STORAGE_KEY = 'livesql_lang';   // 테마(livesql_theme)와 같은 방식

/** 저장값 → 브라우저 언어 → 'ko' 순으로 결정. */
export function detectLanguage() {
    try {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved && SUPPORTED_LANGUAGES.some(l => l.code === saved)) return saved;
    } catch (_) { /* 시크릿 모드 등에서 접근 불가 — 아래로 진행 */ }

    const nav = (typeof navigator !== 'undefined' && (navigator.language || navigator.userLanguage)) || '';
    // 'en-US' → 'en'. 지원하지 않는 언어면 한국어.
    const base = String(nav).split('-')[0].toLowerCase();
    return SUPPORTED_LANGUAGES.some(l => l.code === base) ? base : 'ko';
}

export function setLanguage(code) {
    if (!SUPPORTED_LANGUAGES.some(l => l.code === code)) return;
    i18n.changeLanguage(code);
    try { localStorage.setItem(STORAGE_KEY, code); } catch (_) { /* 저장 실패는 무시 — 이번 세션에는 적용됨 */ }
    if (typeof document !== 'undefined') document.documentElement.setAttribute('lang', code);
}

i18n
    .use(initReactI18next)
    .init({
        resources: {
            ko: {
                common: koCommon, menu: koMenu, editor: koEditor, connection: koConnection,
                monitoring: koMonitoring, challenge: koChallenge, dataTransfer: koDataTransfer, settings: koSettings, auth: koAuth, assistant: koAssistant,
            },
            en: {
                common: enCommon, menu: enMenu, editor: enEditor, connection: enConnection,
                monitoring: enMonitoring, challenge: enChallenge, dataTransfer: enDataTransfer, settings: enSettings, auth: enAuth, assistant: enAssistant,
            },
        },
        lng: detectLanguage(),
        fallbackLng: 'ko',
        ns: NAMESPACES,
        defaultNS: 'common',
        interpolation: {
            escapeValue: false,   // React 가 이미 이스케이프한다
        },
        returnEmptyString: false, // 빈 문자열이면 fallback(한국어) 으로 넘어가게
    });

if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('lang', i18n.language);
}

export default i18n;
